import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Plus,
  Copy,
  X,
  DollarSign,
  Search,
  Printer,
  MoreHorizontal,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { LeadFilters, type FilterType } from '../components/LeadFilters';
import { LeadForm } from '../components/LeadForm';
import { AssignLeadModal } from '../components/AssignLeadModal';
import { LeadNotesSection } from '../components/LeadNotesSection';
import { CloseLeadDialog, type CloseType } from '../components/CloseLeadDialog';
import { CopyLeadDialog } from '../components/CopyLeadDialog';
import { PrintLeadSheetButton } from '../components/PrintLeadSheetButton';
import { LeadJobsPanel } from '@/features/jobs';
import { useJobsByLead } from '@/features/jobs/hooks/useJobsByLead';
import { useLeads } from '../hooks/useLeads';
import { useReferenceData } from '../hooks/useReferenceData';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Permissions } from '@/types';
import { getLead, getFirstLead, transitionLeadStatus, deleteLead } from '../api/leads-api';
import type { Lead, LeadsQueryParams } from '../types';

export function LeadsPage() {
  const navigate = useNavigate();
  const { id: urlLeadId } = useParams<{ id: string }>();
  const [queryParams, setQueryParams] = useState<LeadsQueryParams>({
    status: undefined,
    assignedToUserId: undefined,
  });
  const [currentFilter, setCurrentFilter] = useState<FilterType>('all-open');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [selectedCloseType, setSelectedCloseType] = useState<CloseType>('NoGo');
  const [isClosingLead, setIsClosingLead] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize selectedLeadId from URL param if present
  const initialLeadId = urlLeadId ? parseInt(urlLeadId, 10) : undefined;
  const [selectedLeadId, setSelectedLeadId] = useState<number | undefined>(
    initialLeadId && !isNaN(initialLeadId) ? initialLeadId : undefined
  );

  // Track if we've loaded the initial URL lead
  const hasLoadedUrlLead = useRef(false);

  usePageTitle(selectedLead ? `Lead - ${selectedLead.customerName}` : 'Leads');

  const { data: leads, isLoading, error, refetch } = useLeads(queryParams);
  const { users } = useReferenceData();
  const { data: jobs, refetch: refetchJobs } = useJobsByLead(selectedLead?.id);
  const { hasPermission } = useAuth();

  // Permission checks
  const canCreateLeads = hasPermission(Permissions.LeadsCreate);
  const canUpdateLeads = hasPermission(Permissions.LeadsUpdate);
  const canDeleteLeads = hasPermission(Permissions.LeadsDelete);
  // Note: canAssignLeads could be used to conditionally show assign functionality
  // const canAssignLeads = hasPermission(Permissions.LeadsAssign);

  const loadLead = useCallback(async (leadId: number) => {
    try {
      const fullLead = await getLead(leadId);
      setSelectedLead(fullLead);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to load lead details');
    }
  }, []);

  // Load lead from URL param if provided (only on initial mount)
  useEffect(() => {
    if (urlLeadId && !hasLoadedUrlLead.current) {
      const leadId = parseInt(urlLeadId, 10);
      if (!isNaN(leadId)) {
        hasLoadedUrlLead.current = true;
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => loadLead(leadId), 0);
      }
    }
  }, [urlLeadId, loadLead]);

  // Optimistically load the first lead on mount (in parallel with the leads list)
  useEffect(() => {
    if (urlLeadId) return;

    let cancelled = false;
    getFirstLead()
      .then((lead) => {
        if (!cancelled) {
          setSelectedLead(lead);
          setSelectedLeadId(lead.id);
          hasLoadedUrlLead.current = true;
          navigate(`/leads/${lead.id}`, { replace: true });
        }
      })
      .catch(() => {
        // No leads exist — that's fine, show empty state
      });

    return () => { cancelled = true; };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLeadSelect = (leadId: number | undefined) => {
    setSelectedLeadId(leadId);
    if (leadId) {
      hasLoadedUrlLead.current = true;
      navigate(`/leads/${leadId}`, { replace: true });
      loadLead(leadId);
    } else {
      navigate('/leads', { replace: true });
      setSelectedLead(null);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setCurrentFilter(filter);

    // Update query params based on filter
    switch (filter) {
      case 'all-open':
        setQueryParams({
          status: undefined,
          assignedToUserId: undefined,
        });
        break;
      case 'unassigned':
        setQueryParams({
          status: 'Unassigned',
          assignedToUserId: undefined,
        });
        break;
      case 'recent-no-gos':
        setQueryParams({
          status: 'NoGo',
          assignedToUserId: undefined,
        });
        break;
      case 'recently-closed':
        setQueryParams({
          status: 'Closed',
          assignedToUserId: undefined,
        });
        break;
      case 'by-estimator':
        // Already handled by onAssignedToChange
        break;
    }
  };

  const handleAssignedToChange = (userId: number | undefined) => {
    setQueryParams((prev) => ({
      ...prev,
      assignedToUserId: userId,
    }));
  };

  const handleNewLead = () => {
    // Focused, chrome-less New Lead entry (AT-20 Phase 5)
    navigate('/leads/new');
  };

  const handleCopyLead = () => {
    if (!selectedLead) {
      alert('Please select a lead first');
      return;
    }
    setIsCopyDialogOpen(true);
  };

  const handleCopyConfirm = (copiedLead: Lead) => {
    setSelectedLeadId(undefined);
    navigate('/leads', { replace: true });
    setSelectedLead(copiedLead);
  };

  const handleFormSuccess = (lead: Lead) => {
    refetch();
    refetchJobs();
    // If this was a new lead, select it
    if (lead.id) {
      handleLeadSelect(lead.id);
    }
  };

  const handleAssignSuccess = () => {
    refetch();
    if (selectedLeadId) {
      loadLead(selectedLeadId);
    }
  };

  const handleCloseClick = () => {
    if (!selectedLead) {
      alert('Please select a lead first');
      return;
    }
    setSelectedCloseType('NoGo');
    setIsCloseDialogOpen(true);
  };

  const openJobCostInNewTab = (jobId: number) => {
    window.open(`/jobs/${jobId}?tab=costs`, '_blank', 'noopener,noreferrer');
  };

  const handleJobCostClick = () => {
    if (!selectedLead || !jobs || jobs.length === 0) {
      alert('No jobs found for this lead');
      return;
    }

    if (jobs.length === 1) {
      // Single job — open directly
      openJobCostInNewTab(jobs[0].id);
      return;
    }

    // Multiple jobs — handled by dropdown menu (see render)
  };

  const handleCloseConfirm = async (closeType: CloseType, reason: string) => {
    if (!selectedLeadId) return;

    setIsClosingLead(true);
    try {
      await transitionLeadStatus(selectedLeadId, closeType, reason);
      setIsCloseDialogOpen(false);
      refetch();
      refetchJobs();
      loadLead(selectedLeadId);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to close lead');
    } finally {
      setIsClosingLead(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!selectedLeadId) return;

    setIsDeleting(true);
    try {
      await deleteLead(selectedLeadId);
      setIsDeleteDialogOpen(false);
      setSelectedLead(null);
      setSelectedLeadId(undefined);
      navigate('/leads', { replace: true });
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to delete lead');
    } finally {
      setIsDeleting(false);
    }
  };

  // Icon button style - red icon color
  const iconButtonClass =
    'flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50';
  const iconClass = 'h-4 w-4 text-[#DC2626]';

  return (
    <div className="min-h-screen bg-background px-5 py-6 space-y-4">
      {/* Main Content Container */}
      <div className="bg-card rounded-[10px] p-5 space-y-4">
        {/* Lead Management Header - Title, Filters, and Actions */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side: Title and Filter Links */}
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-secondary">Lead Management</h2>
            <LeadFilters
              onFilterChange={handleFilterChange}
              onAssignedToChange={handleAssignedToChange}
              currentFilter={currentFilter}
              users={users}
              selectedAssignedToId={queryParams.assignedToUserId}
            />
          </div>

          {/* Right side: Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Action Buttons */}
            {canCreateLeads && (
              <Button
                onClick={handleNewLead}
                variant="outline"
                className={iconButtonClass}
              >
                <Plus className={iconClass} />
                New
              </Button>
            )}

            {canCreateLeads && (
              <Button
                onClick={handleCopyLead}
                disabled={!selectedLead}
                variant="outline"
                className={iconButtonClass}
              >
                <Copy className={iconClass} />
                Copy
              </Button>
            )}

            {canUpdateLeads && selectedLead?.status === 'Unassigned' && (
              <Button
                onClick={() => setIsAssignModalOpen(true)}
                variant="outline"
                className={iconButtonClass}
              >
                <UserPlus className={iconClass} />
                Assign
              </Button>
            )}

            {canUpdateLeads && (
              <Button
                onClick={handleCloseClick}
                disabled={!selectedLead}
                variant="outline"
                className={iconButtonClass}
              >
                <X className={iconClass} />
                Close
              </Button>
            )}

            {jobs && jobs.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={!selectedLead}
                    variant="outline"
                    className={iconButtonClass}
                  >
                    <DollarSign className={iconClass} />
                    Job Cost
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {jobs.map((job) => (
                    <DropdownMenuItem
                      key={job.id}
                      onClick={() => openJobCostInNewTab(job.id)}
                    >
                      <span className="font-medium">{job.jobNumber}</span>
                      <span className="text-muted-foreground ml-2">{job.status}</span>
                      {job.assignedTo && (
                        <span className="text-muted-foreground ml-2">— {job.assignedTo}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                disabled={!selectedLead || !jobs || jobs.length === 0}
                variant="outline"
                className={iconButtonClass}
                onClick={handleJobCostClick}
              >
                <DollarSign className={iconClass} />
                Job Cost
              </Button>
            )}

            <Button
              variant="outline"
              className={iconButtonClass}
              onClick={() => navigate('/leads/search')}
            >
              <Search className={iconClass} />
              Search
            </Button>

            {selectedLead && selectedLead.id > 0 ? (
              <PrintLeadSheetButton
                leadId={selectedLead.id}
                jobNumber={selectedLead.jobNumber}
                variant="outline"
                className={iconButtonClass}
                onError={(error) => alert(error)}
              />
            ) : (
              <Button
                disabled
                variant="outline"
                className={iconButtonClass}
              >
                <Printer className={iconClass} />
                Print
              </Button>
            )}

            {/* More menu with Delete option */}
            {canDeleteLeads && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={iconButtonClass}
                    disabled={!selectedLead}
                  >
                    <MoreHorizontal className={iconClass} />
                    More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Lead
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Lead Dropdown - full width, own row */}
        <div className="w-full">
          <Select
            value={selectedLeadId?.toString() || ''}
            onChange={(e) => handleLeadSelect(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full h-9 rounded-md border border-border px-3 text-sm"
          >
            <option value="">{isLoading ? 'Loading...' : 'Select Lead'}</option>
            {leads.map((lead) => (
              <option key={lead.id} value={lead.id}>
                {lead.leadTitle || lead.customerName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <ErrorAlert error={error} className="rounded-[20px] p-2" />

      {/* Lead Form - Inline Display */}
      {!error && (selectedLead !== null || selectedLeadId === undefined) && (
        <LeadForm
          lead={selectedLead}
          onSuccess={handleFormSuccess}
          readOnly={selectedLead && selectedLead.id > 0 ? !canUpdateLeads : !canCreateLeads}
        />
      )}

      {/* Jobs Section - Show jobs associated with this lead (one-to-many) */}
      {selectedLead && selectedLead.id > 0 && (
        <LeadJobsPanel key={`${selectedLead.id}-${selectedLead.updatedAt}`} lead={selectedLead} />
      )}

      {/* Lead Notes Section */}
      {selectedLead && selectedLead.id > 0 && (
        <LeadNotesSection leadId={selectedLead.id} />
      )}

      {/* Assign Lead Modal */}
      {selectedLeadId && (
        <AssignLeadModal
          leadId={selectedLeadId}
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Close Lead Dialog */}
      <CloseLeadDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        closeType={selectedCloseType}
        onConfirm={handleCloseConfirm}
        isLoading={isClosingLead}
      />

      {/* Delete Lead Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this lead? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLead}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Lead Dialog */}
      {selectedLead && (
        <CopyLeadDialog
          open={isCopyDialogOpen}
          onOpenChange={setIsCopyDialogOpen}
          lead={selectedLead}
          onConfirm={handleCopyConfirm}
        />
      )}
    </div>
  );
}
